import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, ExternalLink, Loader2, FileText } from "lucide-react";

import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Reusable file uploader for authenticated PDF manuals.
 *
 * Props:
 *  - files: array of { id, filename, size, source? } already attached
 *  - onAttach(file)   : called with the freshly-uploaded file record
 *  - onDetach(fileId) : called when the user removes a file
 *  - accept="application/pdf"
 *  - kind="manual"
 *  - readOnly=false
 *  - testIdPrefix
 */
export default function FileUploader({
  files = [],
  onAttach,
  onDetach,
  accept = "application/pdf",
  kind = "manual",
  readOnly = false,
  testIdPrefix = "uploader",
  emptyLabel = "No manuals attached",
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFiles = useCallback(
    async (list) => {
      if (!list?.length) return;
      setUploading(true);
      for (const f of list) {
        try {
          const form = new FormData();
          form.append("file", f);
          form.append("kind", kind);
          const { data } = await api.post("/files", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          onAttach && (await onAttach(data));
          toast.success(`Uploaded ${data.filename}`);
        } catch (err) {
          toast.error(formatApiError(err));
        }
      }
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    },
    [kind, onAttach],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      if (readOnly) return;
      handleFiles(Array.from(e.dataTransfer.files || []));
    },
    [handleFiles, readOnly],
  );

  const authedUrl = (fileId) => `${API_BASE}/files/${fileId}`;

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={[
            "cursor-pointer border-2 border-dashed rounded-sm p-6 text-center transition-colors",
            dragOver ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40",
          ].join(" ")}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            data-testid={`${testIdPrefix}-input`}
          />
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Upload className="h-6 w-6" />
            )}
            <div className="text-sm font-medium text-foreground">
              {uploading ? "Uploading…" : "Drop PDF here or click to browse"}
            </div>
            <div className="text-xs">Up to 25 MB per file · PDF only</div>
          </div>
        </div>
      )}

      <ul className="space-y-1.5">
        {files.length === 0 && (
          <li className="text-xs text-muted-foreground italic" data-testid={`${testIdPrefix}-empty`}>
            {emptyLabel}
          </li>
        )}
        {files.map((f) => (
          <li
            key={f.id}
            data-testid={`${testIdPrefix}-file-${f.id}`}
            className="flex items-center gap-3 border border-border rounded-sm px-3 py-2 bg-card"
          >
            <FileText className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{f.filename}</div>
              <div className="text-xs text-muted-foreground">
                {formatSize(f.size)}
                {f.source && (
                  <span className="ml-2 uppercase tracking-wider text-[10px] font-bold">
                    {f.source === "machine_type" ? "from type" : "from machine"}
                  </span>
                )}
              </div>
            </div>
            <a
              href={authedUrl(f.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid={`${testIdPrefix}-open-${f.id}`}
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </a>
            {!readOnly && onDetach && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onDetach(f.id)}
                data-testid={`${testIdPrefix}-remove-${f.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
